/**
 * CopfDashboardPage — /copf (Sprint UX Polish)
 *
 * Tableau de bord institutionnel simple pour Admin/COPF.
 * 4 grandes entrées : Athlètes / Contenus / Calendrier / Soutien.
 * Compteurs actionnables cliquables. Petite checklist d'onboarding repliable.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Users, Newspaper, CalendarDays, HandHeart, Flag, ShieldAlert, ArrowRight,
  UserCheck, Copy, ClipboardList, FileSpreadsheet, LifeBuoy, Shirt, ChevronDown, ChevronUp, X,
  ShieldCheck, BookOpen, BarChart3, Bot, Settings,
} from "lucide-react";

const CHECKLIST_KEY = "aito_copf_checklist_v1";
const CHECKLIST_DISMISS_KEY = "aito_copf_checklist_dismissed_v1";

const CHECKLIST_ITEMS = [
  { id: "browse", label: "Consulter l'annuaire des athlètes", to: "/athletes" },
  { id: "import", label: "Importer une liste d'athlètes", to: "/admin/athletes/imports" },
  { id: "claims", label: "Vérifier les revendications", to: "/admin/athletes/imports?tab=claims" },
  { id: "contents", label: "Consulter les contenus à traiter", to: "/admin/contenus" },
  { id: "aides", label: "Proposer une aide sportive", to: "/admin/funding" },
];

export default function CopfDashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState({});
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [checklistDismissed, setChecklistDismissed] = useState(false);
  const roles = user?.roles || [];
  const isStaff = roles.some((r) => ["admin", "copf"].includes(r));

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CHECKLIST_KEY) || "{}");
      setChecklist(saved);
      setChecklistDismissed(localStorage.getItem(CHECKLIST_DISMISS_KEY) === "1");
    } catch {}
  }, []);

  const toggleChecklist = (id) => {
    setChecklist((cur) => {
      const next = { ...cur, [id]: !cur[id] };
      try { localStorage.setItem(CHECKLIST_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const dismissChecklist = () => {
    setChecklistDismissed(true);
    try { localStorage.setItem(CHECKLIST_DISMISS_KEY, "1"); } catch {}
  };

  const load = useCallback(async () => {
    if (!isStaff) { setLoading(false); return; }
    setLoading(true);
    try {
      const [d1, d2] = await Promise.all([
        api.get("/copf/dashboard").catch(() => ({ data: null })),
        api.get("/copf/stats/pending").catch(() => ({ data: null })),
      ]);
      setCounts({ ...(d1.data || {}), ...(d2.data || {}) });
    } catch (e) { setCounts(null); }
    finally { setLoading(false); }
  }, [isStaff]);
  useEffect(() => { load(); }, [load]);

  if (!user) return (
    <div className="max-w-lg mx-auto p-6"><Card className="p-6 text-center">Connexion requise.</Card></div>
  );
  if (!isStaff) return (
    <div className="max-w-lg mx-auto p-6" data-testid="copf-access-denied">
      <Card className="p-6 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
        <h1 className="text-lg font-semibold">Espace réservé COPF / Admin</h1>
        <Button className="mt-4" variant="outline" onClick={() => nav("/")}>Retour à l'accueil</Button>
      </Card>
    </div>
  );

  const doneCount = CHECKLIST_ITEMS.filter((c) => checklist[c.id]).length;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="copf-dashboard-page">
      <div className="mb-4">
        <h1 className="text-2xl md:text-3xl font-display font-bold">Bonjour {user?.name?.split(" ")[0] || "COPF"}</h1>
        <p className="text-sm text-muted-foreground">Votre espace institutionnel AITO.PF · voici ce qui vous attend aujourd'hui.</p>
      </div>

      {/* Checklist repliable */}
      {!checklistDismissed && (
        <Card className="p-3 mb-4 border-l-4 border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/5" data-testid="copf-checklist">
          <div className="flex items-center gap-2">
            <div className="font-display font-semibold text-sm flex-1">Bien démarrer avec AITO.PF <span className="text-xs text-muted-foreground ml-1">({doneCount}/{CHECKLIST_ITEMS.length})</span></div>
            <Button size="sm" variant="ghost" onClick={() => setChecklistOpen((v) => !v)} data-testid="copf-checklist-toggle">
              {checklistOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={dismissChecklist} data-testid="copf-checklist-dismiss" title="Masquer"><X className="h-4 w-4" /></Button>
          </div>
          {checklistOpen && (
            <ul className="mt-2 space-y-1">
              {CHECKLIST_ITEMS.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!checklist[c.id]} onCheckedChange={() => toggleChecklist(c.id)} data-testid={`copf-check-${c.id}`} />
                  <Link to={c.to} className={`flex-1 hover:text-[hsl(var(--aito-lagoon))] ${checklist[c.id] ? "line-through text-muted-foreground" : ""}`}>{c.label}</Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Compteurs actionnables — V9.21 : recentrés sur "À valider" en priorité */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <div data-testid="copf-counters">
          <h2 className="text-sm uppercase tracking-wide text-foreground font-bold mb-3 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /> À valider
          </h2>
          {/* Ligne 1 : décisions COPF prioritaires */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <CounterTile large to="/copf/revendications" icon={ShieldCheck} label="Revendications à traiter" value={counts?.revendications || 0} color="hsl(var(--aito-lagoon))" testid="counter-revendications" alert={counts?.revendications > 0} />
            <CounterTile large to="/copf/memoires" icon={BookOpen} label="Livrets à agréer" value={counts?.livrets_a_agreer || 0} color="hsl(var(--aito-gold))" testid="counter-livrets" alert={counts?.livrets_a_agreer > 0} />
            <CounterTile large to="/admin/athletes/imports?tab=claims" icon={UserCheck} label="Revendications athlètes" value={counts?.claims_pending || 0} color="hsl(var(--aito-gold))" testid="counter-claims" alert={counts?.claims_pending > 0} />
            <CounterTile large to="/admin/athletes/duplicates" icon={Copy} label="Doublons à traiter" value={counts?.duplicates_pending || 0} color="hsl(var(--aito-energy))" testid="counter-duplicates" alert={counts?.duplicates_pending > 0} />
          </div>

          <h2 className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3 flex items-center gap-2">
            <Newspaper className="h-3.5 w-3.5" /> Autres compteurs
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CounterTile to="/athletes" icon={Users} label="Athlètes publiés" value={counts?.athletes_total || 0} color="hsl(var(--aito-lagoon))" testid="counter-athletes" />
            <CounterTile to="/admin/contenus" icon={Newspaper} label="Contenus à traiter" value={counts?.contents_todo || 0} color="hsl(var(--aito-lagoon))" testid="counter-contents" alert={counts?.contents_todo > 0} />
            <CounterTile to="/admin/funding" icon={LifeBuoy} label="Aides à valider" value={counts?.aides_to_review || counts?.aides || 0} color="hsl(var(--aito-energy))" testid="counter-aides" alert={(counts?.aides_to_review || counts?.aides || 0) > 0} />
            <CounterTile to="/calendrier" icon={CalendarDays} label="Événements à venir (90j)" value={counts?.events_upcoming || 0} color="hsl(var(--aito-lagoon))" testid="counter-events" />
          </div>
        </div>
      )}

      {/* Grandes entrées — renommées "Consulter" */}
      <div className="mt-6">
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold flex items-center gap-2">
          <Newspaper className="h-3.5 w-3.5" /> Consulter
        </div>
        <div className="grid md:grid-cols-2 gap-3" data-testid="copf-entries">
          <SpaceCard to="/copf/reporting-palmares" title="Reporting Palmarès Multi-Sports" desc="Pilotage transversal · filtres discipline · fédé · année · médaille" icon={BarChart3} testid="entry-reporting-palmares" />
          <SpaceCard to="/admin/ai-athletes" title="Fiches créées par IA" desc="Détection Curator · À enrichir en priorité" icon={Bot} testid="entry-ai-athletes" />
          <SpaceCard to="/admin/site-settings" title="Paramètres du site" desc="Compteur JP 2027 · Bannière Mémoire · Livres" icon={Settings} testid="entry-site-settings" />
          <SpaceCard to="/athletes" title="Athlètes" desc="Annuaire · imports · doublons · revendications" icon={Users} testid="entry-athletes" />
          <SpaceCard to="/admin/contenus" title="Contenus" desc="Veille · validation · publication · mise en avant" icon={Newspaper} testid="entry-contents" />
          <SpaceCard to="/calendrier" title="Calendrier" desc="Événements et compétitions à venir" icon={CalendarDays} testid="entry-calendar" />
          <SpaceCard to="/soutien" title="Soutien aux sportifs" desc="Aides publiques · campagnes AITO x Athlète" icon={HandHeart} testid="entry-soutien" />
          <SpaceCard to="/road-2027" title="Jeux du Pacifique 2027" desc="Suivi des athlètes ayant déclaré viser les JP 2027" icon={Flag} testid="entry-road2027" />
          {roles.includes("admin") && (
            <SpaceCard to="/admin" title="Espace administration" desc="Modération · corbeille · outils avancés" icon={ShieldAlert} testid="entry-admin" />
          )}
        </div>
      </div>
    </div>
  );
}

function CounterTile({ to, icon: Icon, label, value, color, testid, alert, large }) {
  return (
    <Link to={to} className="block group" data-testid={testid}>
      <Card className={`${large ? "p-4" : "p-3"} h-full transition ${alert ? "border-2 border-[hsl(var(--aito-energy))]/70 bg-[hsl(var(--aito-energy))]/10 shadow-sm" : "hover:border-[hsl(var(--aito-lagoon))]"}`}>
        <div className="flex items-center gap-2 mb-1">
          <div className={`${large ? "h-10 w-10" : "h-8 w-8"} rounded-md flex items-center justify-center`} style={{ backgroundColor: `${color}20`, color }}>
            <Icon className={large ? "h-5 w-5" : "h-4 w-4"} />
          </div>
          <div className={`${large ? "text-3xl" : "text-2xl"} font-display font-bold ml-auto`} style={{ color: value > 0 && alert ? color : undefined }}>{value}</div>
        </div>
        <div className={`${large ? "text-xs" : "text-[11px]"} uppercase tracking-wide text-muted-foreground leading-tight`}>{label}</div>
      </Card>
    </Link>
  );
}

function SpaceCard({ to, title, desc, icon: Icon, testid }) {
  return (
    <Link to={to} className="block group" data-testid={testid}>
      <Card className="p-4 h-full hover:border-[hsl(var(--aito-lagoon))] transition">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-[hsl(var(--aito-lagoon))]/10 flex items-center justify-center text-[hsl(var(--aito-lagoon))]">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-bold">{title}</div>
            <div className="text-xs text-muted-foreground">{desc}</div>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition" />
        </div>
      </Card>
    </Link>
  );
}
